/**
 * SIM status + (currently-disabled) SIM-switch API client. Migration
 * 108 backend, Jason 2026-08-03.
 *
 *   GET  /api/boxes/{scu}/peplink/sim-status           per-slot carrier + active
 *   POST /api/boxes/{scu}/peplink/sim-switch           request a switch  (501 default)
 *   GET  /api/boxes/{scu}/peplink/sim-switch/log       audit trail
 *
 * The switch call returns 501 by default because
 * `MttrSettings.PeplinkSimSwitchEnabled=false` and the local Peplink
 * admin API's wire spec hasn't been captured yet. The frontend
 * renders the buttons as disabled until we're comfortable enabling
 * the flag — see components/SimControlCard.tsx.
 */
import { apiBase } from "./apiClient";

export type SimSlotName = "A" | "B";

export interface SimStatusSlot {
  slot: SimSlotName;
  carrier: string | null;
  iccid: string | null;
  imsi: string | null;
  sim_kind: "physical" | "esim" | "multicarrier" | null;
  is_active: boolean;
  signal_dbm: number | null;
  source: "local_api" | "event_history";
  last_seen_utc: string | null;
}

export interface SimStatus {
  scu_name: string;
  device_sn: string;
  slots: SimStatusSlot[];
}

export interface SimSwitchLogRow {
  log_id: number;
  device_sn: string;
  requested_by_user_id: number;
  requested_by_email: string | null;
  requested_utc: string | null;
  from_sim: SimSlotName | null;
  to_sim: SimSlotName;
  outcome: "not_enabled" | "unreachable" | "invoked" | "success" | "error";
  http_status: number | null;
  response_snippet: string | null;
}

export interface SimSwitchResult {
  ok?: boolean;
  outcome?: string;
  http_status?: number | null;
  from_sim?: SimSlotName | null;
  to_sim?: SimSlotName;
  log_id?: number;
  response?: string | null;
  /** 501-body fields — surface these to the operator so they know why. */
  error?: string;
  detail?: string;
}

/** Special variant that returns the parsed body even on 4xx/5xx —
 *  the SIM-switch route intentionally uses 501/502 for expected
 *  outcomes (feature off, box unreachable) and the UI needs the
 *  body to explain them. Only fetch errors throw. */
async function reqBoth<T>(
  token: string, path: string, init?: RequestInit,
): Promise<{ status: number; body: T }> {
  const r = await fetch(`${apiBase()}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      "access-token": token,
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
    cache: "no-store",
  });
  let body: unknown;
  try {
    body = await r.json();
  } catch {
    body = {};
  }
  return { status: r.status, body: body as T };
}

async function req<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  const { status, body } = await reqBoth<T & { error?: string }>(token, path, init);
  if (status < 200 || status >= 300) {
    throw new Error(body.error || `HTTP ${status}`);
  }
  return body;
}

export function fetchSimStatus(
  token: string, scu: string,
): Promise<SimStatus> {
  return req(token, `/api/boxes/${encodeURIComponent(scu)}/peplink/sim-status`);
}

export function fetchSimSwitchLog(
  token: string, scu: string,
): Promise<{ data: SimSwitchLogRow[] }> {
  return req(token, `/api/boxes/${encodeURIComponent(scu)}/peplink/sim-switch/log`);
}

/**
 * Ask the backend to switch to `sim`. Returns the full response body
 * regardless of status so the caller can distinguish 501 (feature off)
 * from 502 (unreachable) from 200 (success). Only network errors throw.
 */
export function requestSimSwitch(
  token: string, scu: string, sim: SimSlotName,
): Promise<{ status: number; body: SimSwitchResult }> {
  return reqBoth<SimSwitchResult>(
    token, `/api/boxes/${encodeURIComponent(scu)}/peplink/sim-switch`,
    { method: "POST", body: JSON.stringify({ sim }) },
  );
}
